import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import styles from './ExpressCheckoutSection.module.css'
import { Skeleton } from '../../components'

import type { StripeElementsMethodPresentation } from '@checkout/core'
import type {
  Stripe,
  StripeElements,
  StripeExpressCheckoutElement,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js'

type StripeWalletMethodId = 'apple_pay' | 'google_pay'
type StripeWalletAvailabilityKey = 'applePay' | 'googlePay'

const STRIPE_WALLET_BUTTON_HEIGHT = 55

export class StripeExpressCheckoutError extends Error {
  readonly code: string
  readonly raw: unknown

  constructor(message: string, code: string, raw?: unknown) {
    super(message)
    this.name = 'StripeExpressCheckoutError'
    this.code = code
    this.raw = raw
  }
}

export interface StripeExpressCheckoutSectionHandle {
  handleNextAction: (clientSecret: string) => Promise<unknown>
}

export interface StripeExpressCheckoutSectionProps {
  presentation?: StripeElementsMethodPresentation | null
  pmId?: string | null
  /** Immutable checkout-method generation owning this Stripe interaction. */
  interactionKey: string
  returnUrl: string
  isPreparing?: boolean
  onBeforeConfirm: () => boolean
  onConfirmationToken: (confirmationToken: string) => Promise<void>
  onFailure: (error: StripeExpressCheckoutError) => void
  onCancel?: () => void
  /** Reports Stripe.js initialization independently from wallet availability. */
  onSdkReadyChange?: (ready: boolean) => void
  onValidityChange?: (valid: boolean) => void
  className?: string
}

function resolveWalletMethod(pmId: string | null | undefined): StripeWalletMethodId | null {
  if (pmId === 'apple_pay' || pmId === 'google_pay') {
    return pmId
  }
  return null
}

function stripeWalletAvailabilityKey(wallet: StripeWalletMethodId): StripeWalletAvailabilityKey {
  return wallet === 'apple_pay' ? 'applePay' : 'googlePay'
}

function createExpressCheckoutOptions(
  wallet: StripeWalletMethodId
): StripeExpressCheckoutElementOptions {
  return {
    buttonHeight: STRIPE_WALLET_BUTTON_HEIGHT,
    buttonTheme: {
      applePay: 'black',
      googlePay: 'black',
    },
    buttonType: {
      applePay: 'plain',
      googlePay: 'plain',
    },
    layout: {
      maxColumns: 1,
      maxRows: 1,
      overflow: 'auto',
    },
    paymentMethodOrder: [wallet],
    paymentMethods: {
      amazonPay: 'never',
      applePay: wallet === 'apple_pay' ? 'always' : 'never',
      googlePay: wallet === 'google_pay' ? 'always' : 'never',
      klarna: 'never',
      link: 'never',
      paypal: 'never',
    },
  }
}

function resolveWalletAvailability(
  event: StripeExpressCheckoutElementReadyEvent,
  wallet: StripeWalletMethodId
): boolean {
  return event.availablePaymentMethods?.[stripeWalletAvailabilityKey(wallet)] === true
}

function stripeError(error: unknown, fallback: string, code: string): StripeExpressCheckoutError {
  const message = error instanceof Error && error.message ? error.message : fallback
  return new StripeExpressCheckoutError(message, code, error)
}

export const StripeExpressCheckoutSection = forwardRef<
  StripeExpressCheckoutSectionHandle,
  StripeExpressCheckoutSectionProps
>(function StripeExpressCheckoutSection(props, ref) {
  const {
    presentation,
    pmId,
    interactionKey,
    returnUrl,
    isPreparing = false,
    onBeforeConfirm,
    onConfirmationToken,
    onFailure,
    onCancel,
    onSdkReadyChange,
    onValidityChange,
    className,
  } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const stripeRef = useRef<Stripe | null>(null)
  const onBeforeConfirmRef = useRef(onBeforeConfirm)
  const onConfirmationTokenRef = useRef(onConfirmationToken)
  const onFailureRef = useRef(onFailure)
  const onCancelRef = useRef(onCancel)
  const onSdkReadyChangeRef = useRef(onSdkReadyChange)
  const onValidityChangeRef = useRef(onValidityChange)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const walletMethod = resolveWalletMethod(pmId ?? presentation?.payload.payment_method)
  const hasPresentation = presentation !== null && presentation !== undefined
  const publishableKey = presentation?.payload.publishable_key.trim() ?? ''
  const elementsMode = presentation?.payload.elements_mode
  const paymentMethodCreation = presentation?.payload.payment_method_creation
  const amount = presentation?.payload.amount
  const currency = presentation?.payload.currency
  const paymentMethodTypes = presentation?.payload.payment_method_types.join(',') ?? ''

  useEffect(() => {
    onBeforeConfirmRef.current = onBeforeConfirm
    onConfirmationTokenRef.current = onConfirmationToken
    onFailureRef.current = onFailure
    onCancelRef.current = onCancel
    onSdkReadyChangeRef.current = onSdkReadyChange
    onValidityChangeRef.current = onValidityChange
  }, [
    onBeforeConfirm,
    onCancel,
    onConfirmationToken,
    onFailure,
    onSdkReadyChange,
    onValidityChange,
  ])

  const reportFailure = useCallback((failure: StripeExpressCheckoutError) => {
    setIsLoading(false)
    setError(failure.message)
    onValidityChangeRef.current?.(false)
    onFailureRef.current(failure)
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      handleNextAction: async (clientSecret: string) => {
        const stripe = stripeRef.current
        if (!stripe) {
          throw new StripeExpressCheckoutError(
            'Stripe checkout is unavailable',
            'STRIPE_CHECKOUT_UNAVAILABLE'
          )
        }
        try {
          const result = await stripe.handleNextAction({ clientSecret })
          if (result.error) {
            throw new StripeExpressCheckoutError(
              result.error.message || 'Stripe wallet authentication failed',
              'STRIPE_NEXT_ACTION_FAILED',
              result.error
            )
          }
          return result
        } catch (error) {
          if (error instanceof StripeExpressCheckoutError) {
            throw error
          }
          throw stripeError(
            error,
            'Stripe wallet authentication failed',
            'STRIPE_NEXT_ACTION_FAILED'
          )
        }
      },
    }),
    []
  )

  useEffect(() => {
    if (!hasPresentation) {
      stripeRef.current = null
      onSdkReadyChangeRef.current?.(false)
      setError(null)
      setIsLoading(true)
      onValidityChangeRef.current?.(false)
      return
    }
    if (!walletMethod) {
      reportFailure(
        new StripeExpressCheckoutError(
          'Stripe express checkout requires a wallet payment method',
          'STRIPE_EXPRESS_CHECKOUT_METHOD_REQUIRED'
        )
      )
      return
    }

    if (
      !publishableKey ||
      !elementsMode ||
      paymentMethodCreation !== 'manual' ||
      amount === undefined ||
      !currency ||
      !paymentMethodTypes
    ) {
      reportFailure(
        new StripeExpressCheckoutError(
          'Stripe publishable key is required',
          'STRIPE_PUBLISHABLE_KEY_REQUIRED'
        )
      )
      return
    }
    const container = containerRef.current
    if (!container) {
      return
    }

    let active = true
    let expressCheckout: StripeExpressCheckoutElement | null = null

    const reportActiveFailure = (failure: StripeExpressCheckoutError) => {
      if (active) {
        reportFailure(failure)
      }
    }

    const rejectSupersededInteraction = (event: StripeExpressCheckoutElementConfirmEvent) => {
      event.paymentFailed({ reason: 'fail', message: 'Payment method changed' })
    }

    const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
      if (!active) {
        return
      }
      if (!resolveWalletAvailability(event, walletMethod)) {
        reportActiveFailure(
          new StripeExpressCheckoutError(
            'Stripe wallet payment method is unavailable in this browser',
            'STRIPE_WALLET_UNAVAILABLE',
            event
          )
        )
        return
      }
      setIsLoading(false)
      setError(null)
      onValidityChangeRef.current?.(true)
    }

    const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
      if (active && onBeforeConfirmRef.current()) {
        event.resolve()
      } else {
        event.reject()
      }
    }

    const mountExpressCheckout = async () => {
      setIsLoading(true)
      setError(null)
      onSdkReadyChangeRef.current?.(false)
      onValidityChangeRef.current?.(false)

      try {
        const { loadStripe } = await import('@stripe/stripe-js/pure')
        const stripe = await loadStripe(publishableKey)
        if (!active) {
          return
        }
        if (!stripe) {
          throw new StripeExpressCheckoutError('Stripe.js is unavailable', 'STRIPE_JS_UNAVAILABLE')
        }

        const elements: StripeElements = stripe.elements({
          mode: elementsMode,
          paymentMethodCreation,
          amount,
          currency,
          paymentMethodTypes: paymentMethodTypes.split(','),
        })
        stripeRef.current = stripe
        onSdkReadyChangeRef.current?.(true)

        const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
          try {
            const submitResult = await elements.submit()
            if (!active) {
              rejectSupersededInteraction(event)
              return
            }
            if (submitResult.error) {
              const failure = new StripeExpressCheckoutError(
                submitResult.error.message || 'Stripe wallet payment details are invalid',
                'STRIPE_ELEMENTS_SUBMIT_FAILED',
                submitResult.error
              )
              event.paymentFailed({ reason: 'invalid_payment_data', message: failure.message })
              reportActiveFailure(failure)
              return
            }

            const tokenResult = await stripe.createConfirmationToken({
              elements,
              params: { return_url: returnUrl },
            })
            if (!active) {
              rejectSupersededInteraction(event)
              return
            }
            if (tokenResult.error) {
              const failure = new StripeExpressCheckoutError(
                tokenResult.error.message || 'Stripe wallet tokenization failed',
                'STRIPE_CONFIRMATION_TOKEN_FAILED',
                tokenResult.error
              )
              event.paymentFailed({ reason: 'invalid_payment_data', message: failure.message })
              reportActiveFailure(failure)
              return
            }

            await onConfirmationTokenRef.current(tokenResult.confirmationToken.id)
            if (!active) {
              rejectSupersededInteraction(event)
            }
          } catch (error) {
            if (!active) {
              rejectSupersededInteraction(event)
              return
            }
            const failure =
              error instanceof StripeExpressCheckoutError
                ? error
                : stripeError(
                    error,
                    'Stripe wallet payment failed',
                    'STRIPE_EXPRESS_CHECKOUT_CONFIRM_FAILED'
                  )
            event.paymentFailed({ reason: 'fail', message: failure.message })
            reportActiveFailure(failure)
          }
        }

        expressCheckout = elements.create(
          'expressCheckout',
          createExpressCheckoutOptions(walletMethod)
        )
        expressCheckout.on('ready', handleReady)
        expressCheckout.on('click', handleClick)
        expressCheckout.on('confirm', handleConfirm)
        expressCheckout.on('cancel', () => {
          if (active) {
            onCancelRef.current?.()
          }
        })
        expressCheckout.on('loaderror', (event) => {
          reportActiveFailure(
            new StripeExpressCheckoutError(
              event.error.message || 'Stripe wallet button failed to load',
              'STRIPE_EXPRESS_CHECKOUT_LOAD_FAILED',
              event.error
            )
          )
        })

        container.innerHTML = ''
        expressCheckout.mount(container)
      } catch (error) {
        if (active) {
          reportActiveFailure(
            error instanceof StripeExpressCheckoutError
              ? error
              : stripeError(error, 'Stripe wallet failed', 'STRIPE_JS_LOAD_FAILED')
          )
        }
      }
    }

    void mountExpressCheckout()

    return () => {
      active = false
      stripeRef.current = null
      onSdkReadyChangeRef.current?.(false)
      expressCheckout?.destroy()
      container.innerHTML = ''
      onValidityChangeRef.current?.(false)
    }
  }, [
    amount,
    currency,
    elementsMode,
    hasPresentation,
    interactionKey,
    paymentMethodCreation,
    paymentMethodTypes,
    publishableKey,
    reportFailure,
    returnUrl,
    walletMethod,
  ])

  if (!presentation && !isPreparing) {
    return null
  }

  const showSkeleton = !error && (isPreparing || isLoading)

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <div className={styles.buttonShell}>
        {showSkeleton && (
          <div className={styles.skeletonLayer}>
            <Skeleton height="60px" style={{ borderRadius: '4px' }} />
          </div>
        )}
        <div
          ref={containerRef}
          className={styles.mount}
          style={{
            opacity: showSkeleton ? 0 : 1,
            pointerEvents: showSkeleton ? 'none' : undefined,
          }}
          aria-hidden={showSkeleton}
        />
      </div>
      {error && <div className={styles.errorText}>{error}</div>}
    </div>
  )
})
